import React, { useEffect, useState } from 'react';
import { FaTimes, FaChevronRight, FaSearch } from 'react-icons/fa';
import axios from '../../api/axios';

interface Hospital {
  id: string;
  name: string;
}

interface HospitalSelectionProps {
  isOpen: boolean;
  onClose: () => void;
  onHospitalSelect: (hospital: Hospital) => void;
  token: string;
}

const HospitalSelection: React.FC<HospitalSelectionProps> = ({
  isOpen,
  onClose,
  onHospitalSelect,
  token,
}) => {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [filteredHospitals, setFilteredHospitals] = useState<Hospital[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        setIsLoading(true);
        // TODO: GANTI URL INI DENGAN ENDPOINT YANG BENAR
        const response = await axios.get('primaya/organizations', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Adjust mapping based on actual API response structure
        const data = response.data.data || response.data;
        setHospitals(data || []);
        setFilteredHospitals(data || []);
      } catch (error) {
        console.error('Error fetching hospitals:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchHospitals();
      setSearchQuery('');
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredHospitals(hospitals);
    } else {
      const filtered = hospitals.filter((hospital) =>
        hospital.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredHospitals(filtered);
    }
  }, [searchQuery, hospitals]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-lg relative max-h-[80vh] flex flex-col">
        <button className="absolute top-2 right-2 text-gray-600" onClick={onClose}>
          <FaTimes />
        </button>
        <div className="text-center mb-4">
          <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-2" />
          <h3 className="text-lg font-semibold text-teal-600">Pilih Rumah Sakit Tujuan</h3>
        </div>

        <div className="mb-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Cari rumah sakit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            />
          </div>
        </div>

        <div className="space-y-3 overflow-y-auto flex-1">
          {isLoading ? (
            <p className="text-sm text-gray-600 text-center">Memuat daftar rumah sakit...</p>
          ) : filteredHospitals.length > 0 ? (
            filteredHospitals.map((hospital) => (
              <button
                key={hospital.id}
                className="w-full flex justify-between items-center p-3 bg-gray-100 rounded-lg hover:bg-gray-200 text-left text-sm font-medium"
                onClick={() => {
                  onHospitalSelect(hospital);
                }}
              >
                {hospital.name}
                <FaChevronRight className="text-gray-400" />
              </button>
            ))
          ) : (
            <p className="text-sm text-gray-600 text-center">
              {searchQuery ? 'Tidak ada rumah sakit yang sesuai.' : 'Tidak ada rumah sakit tersedia.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HospitalSelection;
